import "devextreme/dist/css/dx.material.blue.light.compact.css";
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import VueFinalModal from "vue-final-modal";
import VCalendar from "v-calendar";
import Quasar from 'quasar/src/vue-plugin.js';;
import quasarUserOptions from "./quasar-user-options";
import axios from "axios";
import SocialSharing from "vue-social-sharing";
import VueTour from "v3-tour";
import authService from "./services/auth.service";
import CKEditor from "@ckeditor/ckeditor5-vue";
import CountryFlag from "vue-country-flag-next";
import { initializeI18n } from "./i18n";
import store from "./store/index";
import requestState from "./helpers/requestState";
import Cookies from "js-cookie";
import cfg from "devextreme/core/config";
import { licenseKey } from "./devextreme-license";

cfg({ licenseKey });
require("v3-tour/dist/vue-tour.css");

initializeI18n().then((i18n) => {
  const app = createApp(App)
    .use(i18n)
    .use(router)
    .use(CKEditor)
    .use(Quasar, quasarUserOptions)
    .use(VueFinalModal())
    .use(VCalendar, {})
    .use(SocialSharing)
    .use(VueTour);

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // ignore refreshing token if the first try fails
      if (originalRequest.url.includes("/auth/refresh")) {
        if (!requestState.isLogoutInProgress) {
          requestState.isLogoutInProgress = true;
          const returnUrl = originalRequest.url.includes("/api")
            ? ""
            : originalRequest.url;
          router.push({
            name: "Login",
            query: { returnUrl },
          });
        }
        return Promise.reject(error);
      }

      // if authentication fails, try to get new access token
      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (requestState.isLogoutInProgress) {
          return Promise.reject(error);
        }

        try {
          if (requestState.isRateLimitActive) {
            return Promise.reject(error);
          }

          if (!requestState.isRefreshing) {
            requestState.isRefreshing = true;
            requestState.refreshPromise = authService
              .refreshToken()
              .then(() => authService.getAuthUserData(false, ""))
              .finally(() => {
                requestState.isRefreshing = false;
              });
          }
          // wait for the refreshPromise
          await requestState.refreshPromise;
          // Retry the originalRequest
          return axios(originalRequest);
        } catch (refreshTokenError) {
          // if server returns 429 (too many requests), pause all requests for 30 seconds
          if (
            refreshTokenError.response &&
            refreshTokenError.response.status === 429
          ) {
            requestState.isRateLimitActive = true;

            store.commit("auth/enableModalError");
            store.commit(
              "auth/updateModalError",
              refreshTokenError.response.data
            );

            await authService.logout(false);
            requestState.isRefreshing = false;
            requestState.isLogoutInProgress = false;
            requestState.refreshPromise = null;

            await router.push({
              name: "Login",
            });

            setTimeout(() => {
              requestState.isRateLimitActive = false;
            }, 60000);
            return Promise.reject(refreshTokenError);
          } else if (
            refreshTokenError.response &&
            refreshTokenError.response.status === 403
          ) {
            // Refresh token is not valid, clear authUser and don't call logout on the server side
            await authService.logout(false);
            requestState.isRefreshing = false;
            requestState.isLogoutInProgress = false;
            requestState.refreshPromise = null;
            const returnUrl = originalRequest.url.includes("/api")
              ? ""
              : originalRequest.url;
            router.push({
              name: "Login",
              query: { returnUrl },
            });

            return Promise.reject(refreshTokenError);
          } else {
            await authService.logout(false);
            requestState.isRefreshing = false;
            requestState.isLogoutInProgress = false;
            requestState.refreshPromise = null;
            return Promise.reject(refreshTokenError);
          }
        }
      }

      // if authorization fails, log out user
      if (error.response && error.response.status === 403) {
        store.commit("auth/enableModalError");
        store.commit("auth/updateModalError", error.response.data);
        await authService.logout(!originalRequest.url.includes("/auth/logout"));
        requestState.isRefreshing = false;
        requestState.isLogoutInProgress = false;
        requestState.refreshPromise = null;

        router.push({
          name: "Login",
        });
      }

      return Promise.reject(error);
    }
  );

  const { locale } = i18n.global;

  axios.interceptors.request.use(function (config) {
    config.headers["Accept-Language"] = locale.value ? locale.value : "nl";

    var xsrfCookieKey = localStorage.getItem("xsrf-cookie-key");
    if (xsrfCookieKey) {
      const xsrfToken = Cookies.get(xsrfCookieKey);
      if (xsrfToken && ["post", "put", "delete"].includes(config.method)) {
        config.headers["X-XSRF-TOKEN"] = xsrfToken;
      }
    }
    return config;
  });

  // Set globalProperties to include i18n's global composer instance
  const { t } = i18n.global;
  app.config.globalProperties.t = t;

  app.config.globalProperties.axios = axios;

  router.isReady().then(async () => {
    app.mount("#app");
  });

  app.component("country-flag", CountryFlag);
});
